import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import { toast } from "sonner";
import {
  getStockLots, createStockLot, updateStockLot, deleteStockLot, rebatchStockLot,
  takeLotOutOfFreezer, getProductSettings, setStockLotRemaining, getLossReasons,
  getStockAvailability, getProductions, getPackagingOptions,
  getProductPackaging, packagingMapFromList, allowedPackagings, fmt,
} from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Plus, Trash2, Pencil, Package, PackageX, Eye, FilterX, Snowflake, ThermometerSnowflake, SlidersHorizontal, CalendarClock, ChevronsUpDown, Check, AlertTriangle } from "lucide-react";
import { useT } from "@/i18n/I18nContext";
import { formatDate } from "@/lib/dates";
import { useProductTypes } from "@/context/ProductTypesContext";
import { useAuth } from "@/context/AuthContext";
import { usePagination } from "@/hooks/usePagination";
import { TablePagination } from "@/components/TablePagination";

const emptyLot = { id: null, product_type: "Telemea", unit: "kg", quantity: "", expiry_date: "", entry_date: "", production_id: "none", production_date: "", notes: "", storage_state: "frozen" };

// Add whole days to a YYYY-MM-DD date, returning YYYY-MM-DD (used for refrigerated expiry preview).
function addDays(dateStr, days) {
  if (!dateStr || !days) return "";
  const d = new Date(dateStr + "T00:00:00Z");
  if (isNaN(d.getTime())) return "";
  d.setUTCDate(d.getUTCDate() + Number(days));
  return d.toISOString().slice(0, 10);
}

function StorageBadge({ state }) {
  const { t } = useT();
  if (!state) return null;
  if (state === "refrigerated") {
    return <Badge className="badge-warn gap-1 text-[10px]" data-testid="storage-badge-refrigerated"><ThermometerSnowflake className="h-3 w-3" /> {t("stock.refrigerated")}</Badge>;
  }
  return <Badge variant="secondary" className="gap-1 text-[10px]" data-testid="storage-badge-frozen"><Snowflake className="h-3 w-3" /> {t("stock.frozen")}</Badge>;
}

function ExpiryBadge({ d }) {
  const { t } = useT();
  const [now] = useState(() => Date.now());
  if (!d) return null;
  const days = Math.ceil((new Date(d).getTime() - now) / 86400000);
  if (days < 0) return <Badge className="badge-bad">{t("stock.expired")}</Badge>;
  if (days <= 7) return <Badge className="badge-warn">{t("stock.daysLeft", { n: days })}</Badge>;
  return <span className="text-muted-foreground">{formatDate(d)}</span>;
}

export default function Stock() {
  const { t, td } = useT();
  const { can } = useAuth();
  const canDelete = can("admin", "manager");
  const [lots, setLots] = useState([]);
  const [avail, setAvail] = useState([]);
  const [productions, setProductions] = useState([]);
  const [packagings, setPackagings] = useState([]);
  const [pkgMap, setPkgMap] = useState({});
  const [dslMap, setDslMap] = useState({}); // product_type -> delivery_shelf_life_days (only when configured)
  const [freezerMap, setFreezerMap] = useState({}); // product_type -> true when kept in freezer
  const [dialog, setDialog] = useState(null);
  const [detail, setDetail] = useState(null);
  const [adjust, setAdjust] = useState(null); // lot being stock-corrected
  const [fProduct, setFProduct] = useState("all");
  const [fDateField, setFDateField] = useState("production_date"); // "production_date" | "expiry_date"
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [hideOOS, setHideOOS] = useState(true);
  // One-tap "expiring soon" FILTER — shows only lots expiring within 7 days.
  // Deep-linkable via /stock?expiring=1 (used by the bell notifications).
  const [searchParams, setSearchParams] = useSearchParams();
  const [expiringOnly, setExpiringOnly] = useState(searchParams.get("expiring") === "1");
  const today = new Date().toISOString().slice(0, 10);

  // A lot is "expiring soon" when it is still in stock and its expiry is within 7 days
  // (already-expired lots are included since their day count is negative).
  const EXPIRING_WINDOW_DAYS = 7;
  const isExpiringSoon = (l) => {
    if (!l.expiry_date || Number(l.remaining) <= 1e-9) return false;
    const days = Math.ceil((new Date(l.expiry_date).getTime() - Date.now()) / 86400000);
    return days <= EXPIRING_WINDOW_DAYS;
  };
  // Expired = past its expiry date with stock still on hand — eligible for one-tap write-off.
  const isExpired = (l) =>
    !!l.expiry_date && String(l.expiry_date).slice(0, 10) < today && Number(l.remaining) > 1e-9;

  const productOptions = useMemo(
    () => Array.from(new Set(lots.map((l) => l.product_type).filter(Boolean))).sort(),
    [lots],
  );
  const baseLots = useMemo(() => lots.filter((l) => {
    if (fProduct !== "all" && l.product_type !== fProduct) return false;
    if (hideOOS && Number(l.remaining) <= 1e-9) return false;
    const raw = fDateField === "expiry_date" ? l.expiry_date : l.production_date;
    const d = (raw || "").slice(0, 10);
    if (fFrom) { if (!d || d < fFrom) return false; }
    if (fTo) { if (!d || d > fTo) return false; }
    return true;
  }), [lots, fProduct, fDateField, fFrom, fTo, hideOOS]);
  const filteredLots = useMemo(
    () => (expiringOnly ? baseLots.filter(isExpiringSoon) : baseLots),
    [baseLots, expiringOnly], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const hasFilters = fProduct !== "all" || fFrom || fTo || expiringOnly;
  const resetFilters = () => { setFProduct("all"); setFFrom(""); setFTo(""); setExpiringOnly(false); setSearchParams({}, { replace: true }); };

  const pager = usePagination(filteredLots, 50);

  // Count from the base list so the button badge stays informative while the filter is off.
  const expiringCount = useMemo(() => baseLots.filter(isExpiringSoon).length, [baseLots]); // eslint-disable-line react-hooks/exhaustive-deps

  // One-tap toggle: filters the table down to expiring lots only (and back).
  const toggleExpiring = () => {
    const next = !expiringOnly;
    if (next && expiringCount === 0) { toast.info(t("stock.noExpiringSoon")); return; }
    setExpiringOnly(next);
    setSearchParams(next ? { expiring: "1" } : {}, { replace: true });
    pager.setPage(1);
  };

  const load = async () => {
    const [l, a, p, pk, pp, ps] = await Promise.all([getStockLots(), getStockAvailability(), getProductions(), getPackagingOptions(), getProductPackaging(), getProductSettings()]);
    setLots(l); setAvail(a); setProductions(p); setPackagings(pk.filter((x) => x.active !== false)); setPkgMap(packagingMapFromList(pp));
    const m = {};
    const fz = {};
    for (const s of ps || []) {
      const dsl = s.delivery_shelf_life_days;
      if (dsl != null && Number(dsl) > 0) m[s.product_type] = Number(dsl);
      if (s.freezer_storage) fz[s.product_type] = true;
    }
    setDslMap(m);
    setFreezerMap(fz);
  };
  useEffect(() => { (async () => { await load(); })(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const save = async (f) => {
    try {
      if (!f.quantity || Number(f.quantity) <= 0) { toast.error(t("stock.qtyGt0")); return; }
      if (f.id) {
        await updateStockLot(f.id, { quantity: Number(f.quantity), unit: f.unit, expiry_date: f.expiry_date || null, entry_date: f.entry_date || null, production_date: f.production_date || null, notes: f.notes, storage_state: freezerMap[f.product_type] ? f.storage_state : undefined });
        toast.success(t("stock.saved"));
      } else if (f.source_lot_id) {
        const res = await rebatchStockLot({
          source_lot_id: f.source_lot_id, unit: f.unit, quantity: Number(f.quantity),
          expiry_date: f.expiry_date || null, notes: f.notes, keep_source: !!f.keep_source,
        });
        if (res.kept_source) {
          toast.success(t("stock.rebatchedKeptToast", { code: res.lot_code, kg: fmt(res.kg_taken, 2) }));
          if (res.warning) toast.warning(res.warning);
        } else {
          toast.success(t("stock.rebatchedToast", { code: res.lot_code, source: res.source_lot_code, kg: fmt(res.kg_taken, 2) }));
        }
      } else {
        const res = await createStockLot({
          product_type: f.product_type, unit: f.unit, quantity: Number(f.quantity),
          expiry_date: f.expiry_date || null, notes: f.notes,
          entry_date: f.entry_date || null,
          production_date: f.production_date || null,
          production_id: f.production_id && f.production_id !== "none" ? f.production_id : null,
          storage_state: freezerMap[f.product_type] ? f.storage_state : undefined,
        });
        const kd = res?.kg_deduction;
        if (kd && kd.required > 0) {
          if (kd.shortfall > 0) {
            toast.warning(t("stock.savedKgShort", { deducted: fmt(kd.deducted, 2), required: fmt(kd.required, 2), short: fmt(kd.shortfall, 2) }));
          } else {
            toast.success(t("stock.savedKgDeducted", { kg: fmt(kd.deducted, 2), product: td(f.product_type) }));
          }
        } else {
          toast.success(t("stock.saved"));
        }
      }
      setDialog(null); load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && typeof d === "object" ? d.message : d) || t("stock.failedSave"));
    }
  };

  const remove = async (id) => {
    try { await deleteStockLot(id); toast.success(t("stock.removed")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("stock.failedDelete")); }
  };

  const takeOut = async (l) => {
    try {
      const res = await takeLotOutOfFreezer(l.id);
      toast.success(t("stock.takeOutSuccess", { code: l.lot_code, date: res.expiry_date ? formatDate(res.expiry_date) : "-" }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("stock.takeOutFailed")); }
  };

  const saveAdjust = async (lot, value, note) => {
    try {
      await setStockLotRemaining(lot.id, { remaining: Number(value), note: note || "" });
      toast.success(t("stock.remainingAdjusted"));
      setAdjust(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("stock.failedSave")); }
  };

  // One-tap write-off with a structured loss reason (built-in + tenant custom reasons) so
  // reports show WHY stock left. Available on any lot with stock; defaults to "expired" when
  // the lot is past its expiry date.
  const BUILTIN_WRITE_OFF_REASONS = ["expired", "damaged", "quality", "other"];
  const [customReasons, setCustomReasons] = useState([]);
  useEffect(() => { getLossReasons().then((d) => setCustomReasons(d.custom || [])).catch(() => {}); }, []);
  const writeOffReasons = [...BUILTIN_WRITE_OFF_REASONS, ...customReasons];
  const reasonLabel = (r) => (BUILTIN_WRITE_OFF_REASONS.includes(r) ? t(`stock.reason.${r}`) : r);
  const [writeOffTarget, setWriteOffTarget] = useState(null);
  const [woReason, setWoReason] = useState("expired");
  const [woNote, setWoNote] = useState("");
  const [woQty, setWoQty] = useState("");
  const openWriteOff = (l) => {
    setWoReason(isExpired(l) ? "expired" : "damaged");
    setWoNote("");
    setWoQty(String(l.remaining));
    setWriteOffTarget(l);
  };
  const confirmWriteOff = async () => {
    const l = writeOffTarget;
    if (!l) return;
    const qty = Number(woQty);
    const rem = Number(l.remaining) || 0;
    if (!Number.isFinite(qty) || qty <= 0 || qty > rem + 1e-9) {
      toast.error(t("stock.writeOffQtyInvalid", { qty: fmt(rem, 2), unit: l.unit }));
      return;
    }
    const newRemaining = Math.max(0, Math.round((rem - qty) * 1000) / 1000);
    const label = reasonLabel(woReason);
    const note = woNote.trim() ? `${label} — ${woNote.trim()}` : label;
    try {
      await setStockLotRemaining(l.id, { remaining: newRemaining, note, reason: woReason });
      toast.success(t("stock.writeOffDone", { code: l.lot_code }));
      setWriteOffTarget(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("stock.failedSave")); }
  };

  // Group live availability per product so the overview stays compact and scannable.
  // Within each product, bulk "kg" is listed first, then packages by descending availability.
  const groupedAvail = useMemo(() => {
    const m = {};
    for (const r of avail) (m[r.product_type] ||= []).push(r);
    return Object.entries(m)
      .map(([product, units]) => ({
        product,
        units: units.slice().sort((a, b) => {
          if (a.unit === "kg") return -1;
          if (b.unit === "kg") return 1;
          return Number(b.available) - Number(a.available);
        }),
      }))
      .sort((a, b) => a.product.localeCompare(b.product));
  }, [avail]);

  // Toggle: hide out-of-stock (available <= 0) rows, dropping products with nothing left.
  const visibleGroupedAvail = useMemo(() => {
    if (!hideOOS) return groupedAvail;
    return groupedAvail
      .map((g) => ({ ...g, units: g.units.filter((r) => Number(r.available) > 1e-9) }))
      .filter((g) => g.units.length > 0);
  }, [groupedAvail, hideOOS]);

  // Total available for one product, expressed in kg. Packed-on-order products expose the same
  // bulk kg pool under several packaging units, so count their pool once (the "kg" row); for the
  // rest, convert each packaging unit to kg via its net weight and sum.
  const groupTotalKg = (units) => {
    if ((units || []).some((r) => r.packed_on_order)) {
      const kgRow = units.find((r) => r.unit === "kg");
      return Math.max(Number(kgRow?.available) || 0, 0);
    }
    let total = 0;
    for (const r of units || []) {
      const net = r.unit === "kg" ? 1 : Number(r.net_kg || 0);
      if (net > 0) total += Math.max(Number(r.available) || 0, 0) * net;
    }
    return total;
  };

  return (
    <Layout actions={
      <Button size="sm" onClick={() => setDialog({ ...emptyLot, entry_date: today })} data-testid="add-lot-button">
        <Plus className="mr-1.5 h-4 w-4" /> {t("stock.addLot")}
      </Button>
    }>
      <div className="space-y-6">
        <div>
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-muted-foreground">{t("stock.liveAvailability")}</h2>
            {avail.length > 0 && (
              <div className="flex items-center gap-2">
                <Switch id="hide-oos-toggle" checked={hideOOS} onCheckedChange={setHideOOS} data-testid="stock-hide-oos-toggle" />
                <Label htmlFor="hide-oos-toggle" className="cursor-pointer text-xs text-muted-foreground">{t("stock.hideOutOfStock")}</Label>
              </div>
            )}
          </div>
          {avail.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("stock.noSellable")}</p>
          ) : visibleGroupedAvail.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="avail-all-oos">{t("stock.allOutOfStock")}</p>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2 xl:grid-cols-3">
              {visibleGroupedAvail.map((g) => (
                <Card key={g.product} data-testid={`avail-product-${g.product}`}>
                  <CardContent className="p-4">
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <span className="truncate font-semibold">{td(g.product)}</span>
                      <Badge variant="secondary" className="shrink-0 text-[10px]">{t("stock.nPackagings", { n: g.units.length })}</Badge>
                    </div>
                    <div className="divide-y divide-border">
                      {g.units.map((r) => {
                        const isOut = Number(r.available) <= 1e-9;
                        return (
                        <div
                          key={r.unit}
                          className={`flex items-center justify-between gap-3 py-2 ${isOut ? "opacity-70" : ""}`}
                          data-testid={`avail-${r.product_type}-${r.unit}`}
                        >
                          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                            <Badge variant="outline" className="font-mono text-[10px]">{r.unit}</Badge>
                            {isOut && (
                              <Badge className="badge-bad text-[10px]" data-testid={`avail-oos-${r.product_type}-${r.unit}`}>{t("stock.outOfStock")}</Badge>
                            )}
                            {r.has_price === false && (
                              <Badge className="badge-warn text-[10px]" data-testid={`avail-noprice-${r.product_type}-${r.unit}`}>{t("stock.noPrice")}</Badge>
                            )}
                          </div>
                          <div className="shrink-0 text-right">
                            <div className={`font-mono text-lg font-semibold leading-none tabular-nums ${isOut ? "text-[hsl(var(--destructive))]" : ""}`}>{fmt(r.available, 2)}</div>
                            <div className="mt-0.5 text-[10px] text-muted-foreground">
                              {t("stock.onHandReservedLine", { oh: fmt(r.on_hand, 2), r: fmt(r.reserved, 2) })}
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                    <div className="mt-2 flex justify-end border-t border-border pt-2" data-testid={`avail-total-${g.product}`}>
                      <span className="text-[11px] text-muted-foreground">
                        {t("stock.totalAvailableKg")}{" "}
                        <span className="font-mono font-semibold tabular-nums text-foreground">{fmt(groupTotalKg(g.units), 2)} kg</span>
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Package className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("stock.title")} ({filteredLots.length}{hasFilters ? `/${lots.length}` : ""})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {lots.length > 0 && (
              <div className="mb-4 flex flex-wrap items-end gap-3" data-testid="stock-filters">
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("common.product")}</Label>
                  <Select value={fProduct} onValueChange={setFProduct}>
                    <SelectTrigger className="h-9 w-[170px] text-sm" data-testid="stock-filter-product"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("stock.allProducts")}</SelectItem>
                      {productOptions.map((p) => <SelectItem key={p} value={p}>{td(p)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("stock.dateField")}</Label>
                  <Select value={fDateField} onValueChange={setFDateField}>
                    <SelectTrigger className="h-9 w-[150px] text-sm" data-testid="stock-filter-datefield"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="production_date">{t("stock.madeOn")}</SelectItem>
                      <SelectItem value="expiry_date">{t("common.expiry")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("subproducts.dateFrom")}</Label>
                  <Input type="date" className="h-9 w-[150px]" value={fFrom} onChange={(e) => setFFrom(e.target.value)} data-testid="stock-filter-from" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("subproducts.dateTo")}</Label>
                  <Input type="date" className="h-9 w-[150px]" value={fTo} onChange={(e) => setFTo(e.target.value)} data-testid="stock-filter-to" />
                </div>
                {hasFilters ? (
                  <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="stock-filter-reset">
                    <FilterX className="mr-1.5 h-4 w-4" /> {t("subproducts.reset")}
                  </Button>
                ) : null}
                <div className="ml-auto">
                  <Button
                    variant={expiringOnly ? "default" : "outline"}
                    size="sm"
                    onClick={toggleExpiring}
                    disabled={expiringCount === 0 && !expiringOnly}
                    aria-pressed={expiringOnly}
                    className={expiringOnly ? "bg-[hsl(var(--warning))] text-white hover:bg-[hsl(var(--warning))]/90" : ""}
                    data-testid="stock-expiring-toggle"
                  >
                    <CalendarClock className="mr-1.5 h-4 w-4" /> {t("stock.expiringSoon")}
                    {expiringCount > 0 && (
                      <span
                        className={`ml-2 rounded-full px-2 py-0.5 text-xs font-medium ${expiringOnly ? "bg-white/25 text-white" : "bg-[hsl(var(--qc-warn-bg))] text-[hsl(var(--warning))]"}`}
                        data-testid="stock-expiring-count"
                      >
                        {expiringCount}
                      </span>
                    )}
                  </Button>
                </div>
              </div>
            )}
            {lots.length === 0 ? (
              <div className="flex flex-col items-center py-12 text-center">
                <PackageX className="h-10 w-10 text-muted-foreground/50" />
                <p className="mt-3 text-sm text-muted-foreground">{t("stock.empty")}</p>
                <Button className="mt-4" size="sm" onClick={() => setDialog({ ...emptyLot, entry_date: today })}>
                  <Plus className="mr-1.5 h-4 w-4" /> {t("stock.addFirstLot")}
                </Button>
              </div>
            ) : filteredLots.length === 0 ? (
              <div className="flex flex-col items-center py-12 text-center" data-testid="stock-no-match">
                <FilterX className="h-10 w-10 text-muted-foreground/50" />
                <p className="mt-3 text-sm text-muted-foreground">{t("stock.noMatch")}</p>
              </div>
            ) : (
              <div className="overflow-x-auto thin-scroll">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("common.lot")}</TableHead>
                      <TableHead>{t("common.product")}</TableHead>
                      <TableHead>{t("common.unit")}</TableHead>
                      <TableHead className="text-right">{t("common.quantity")}</TableHead>
                      <TableHead className="text-right">{t("common.remaining")}</TableHead>
                      <TableHead>{t("stock.madeOn")}</TableHead>
                      <TableHead>{t("common.expiry")}</TableHead>
                      <TableHead>{t("stock.storageState")}</TableHead>
                      <TableHead>{t("stock.fromProductionCol")}</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pager.pageItems.map((l) => {
                      const isOut = Number(l.remaining) <= 1e-9;
                      const expSoon = expiringOnly && isExpiringSoon(l);
                      const rowCls = isOut
                        ? "bg-[hsl(var(--destructive)/0.08)]"
                        : expSoon
                          ? "bg-[hsl(var(--qc-warn-bg))] ring-1 ring-inset ring-[hsl(var(--warning))]"
                          : "";
                      return (
                      <TableRow key={l.id} data-testid={`lot-row-${l.id}`} className={rowCls}>
                        <TableCell className="font-mono text-xs">{l.lot_code}</TableCell>
                        <TableCell className="font-medium">{td(l.product_type)}</TableCell>
                        <TableCell><Badge variant="outline" className="font-mono text-[10px]">{l.unit}</Badge></TableCell>
                        <TableCell className="text-right font-mono tabular-nums">{fmt(l.quantity, 2)}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums">
                          <span className={isOut ? "font-semibold text-[hsl(var(--destructive))]" : ""}>{fmt(l.remaining, 2)}</span>
                          {isOut && <Badge className="badge-bad ml-2 text-[10px]" data-testid={`lot-oos-${l.id}`}>{t("stock.outOfStock")}</Badge>}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{l.production_date || "-"}</TableCell>
                        <TableCell className="text-sm">
                          <ExpiryBadge d={l.expiry_date} />
                          {(() => {
                            if (!l.keep_source || !l.parent_lot_id) return null;
                            const parent = lots.find((p) => p.id === l.parent_lot_id);
                            if (!parent || !parent.expiry_date || !l.expiry_date || parent.expiry_date.slice(0, 10) === l.expiry_date.slice(0, 10)) return null;
                            return (
                              <span className="mt-0.5 block text-[10px] text-[hsl(var(--destructive))]" title={t("stock.expiryMismatchHint")} data-testid={`lot-expiry-mismatch-${l.id}`}>
                                <AlertTriangle className="mr-0.5 inline h-3 w-3" />{t("stock.expiryMismatch", { date: formatDate(parent.expiry_date) })}
                              </span>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="text-sm">{freezerMap[l.product_type] ? <StorageBadge state={l.storage_state || "frozen"} /> : <span className="text-muted-foreground">-</span>}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{l.production_code || "-"}</TableCell>
                        <TableCell className="text-right">
                          {Number(l.remaining) > 1e-9 && (
                            <Button variant="ghost" size="icon" title={t("stock.writeOff")}
                              onClick={() => openWriteOff(l)} data-testid={`lot-writeoff-${l.id}`}>
                              <PackageX className="h-4 w-4 text-[hsl(var(--danger))]" />
                            </Button>
                          )}
                          {freezerMap[l.product_type] && (l.storage_state || "frozen") === "frozen" && Number(l.remaining) > 1e-9 && (
                            <Button variant="ghost" size="icon" title={t("stock.takeOutOfFreezer")} onClick={() => takeOut(l)} data-testid={`lot-takeout-${l.id}`}>
                              <ThermometerSnowflake className="h-4 w-4 text-[hsl(var(--brand))]" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => setDetail(l)} data-testid={`lot-detail-${l.id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" title={t("stock.adjustRemaining")} onClick={() => setAdjust(l)} data-testid={`lot-adjust-${l.id}`}>
                            <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => setDialog({ ...l, production_id: l.production_id || "none", quantity: String(l.quantity), expiry_date: l.expiry_date || "", entry_date: (l.created_at || "").slice(0, 10), storage_state: l.storage_state || "frozen" })} data-testid={`lot-edit-${l.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {canDelete && (
                            <Button variant="ghost" size="icon" onClick={() => remove(l.id)} data-testid={`lot-delete-${l.id}`}>
                              <Trash2 className="h-4 w-4 text-muted-foreground" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <TablePagination
                  page={pager.page} pageCount={pager.pageCount} pageSize={pager.pageSize}
                  total={pager.total} start={pager.start} end={pager.end}
                  onPageChange={pager.setPage} onPageSizeChange={pager.setPageSize}
                  testid="stock-pagination"
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <LotDialog key={dialog ? (dialog.id ?? "new") : "closed"} dialog={dialog} productions={productions} packagings={packagings} pkgMap={pkgMap} lots={lots} dslMap={dslMap} freezerMap={freezerMap} onClose={() => setDialog(null)} onSave={save} />
      <LotDetailDialog detail={detail} dslMap={dslMap} freezerMap={freezerMap} onClose={() => setDetail(null)} />
      <AdjustRemainingDialog lot={adjust} onClose={() => setAdjust(null)} onSave={saveAdjust} />

      {/* One-tap write-off with loss reason */}
      <AlertDialog open={!!writeOffTarget} onOpenChange={(o) => { if (!o) setWriteOffTarget(null); }}>
        <AlertDialogContent data-testid="writeoff-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("stock.writeOffConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {writeOffTarget && t("stock.writeOffConfirmText", {
                code: writeOffTarget.lot_code,
                qty: fmt(writeOffTarget.remaining, 2),
                unit: writeOffTarget.unit,
                product: td(writeOffTarget.product_type),
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="wo-qty">{t("stock.writeOffQtyLabel")}</Label>
              <div className="flex items-center gap-2">
                <Input id="wo-qty" type="number" min="0" step="0.001"
                  max={writeOffTarget ? writeOffTarget.remaining : undefined}
                  value={woQty} onChange={(e) => setWoQty(e.target.value)}
                  className="w-40 text-right font-mono tabular-nums" data-testid="writeoff-qty-input" />
                <span className="text-sm text-muted-foreground">
                  {writeOffTarget && t("stock.writeOffQtyMax", { qty: fmt(writeOffTarget.remaining, 2), unit: writeOffTarget.unit })}
                </span>
                {writeOffTarget && Number(woQty) !== Number(writeOffTarget.remaining) && (
                  <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs"
                    onClick={() => setWoQty(String(writeOffTarget.remaining))} data-testid="writeoff-qty-all">
                    {t("stock.writeOffAll")}
                  </Button>
                )}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("stock.writeOffReason")}</Label>
              <RadioGroup value={woReason} onValueChange={setWoReason} className="grid grid-cols-2 gap-2"
                data-testid="writeoff-reason-group">
                {writeOffReasons.map((r) => (
                  <label key={r} htmlFor={`wo-reason-${r}`}
                    className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${woReason === r ? "border-[hsl(var(--danger))] bg-[hsl(var(--danger)/0.06)]" : "border-border hover:bg-secondary"}`}>
                    <RadioGroupItem value={r} id={`wo-reason-${r}`} data-testid={`writeoff-reason-${r}`} />
                    {reasonLabel(r)}
                  </label>
                ))}
              </RadioGroup>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wo-note">{t("stock.writeOffNoteOptional")}</Label>
              <Input id="wo-note" value={woNote} onChange={(e) => setWoNote(e.target.value)}
                maxLength={200} data-testid="writeoff-note-input" />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="writeoff-cancel-button">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmWriteOff}
              className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger))]/90"
              data-testid="writeoff-confirm-button">
              {t("stock.writeOff")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function AdjustRemainingDialog({ lot, onClose, onSave }) {
  const { t, td } = useT();
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => {
    if (lot) { setValue(String(lot.remaining ?? "")); setNote(""); }
  }, [lot]);
  if (!lot) return null;
  const qty = Number(lot.quantity) || 0;
  const num = Number(value);
  const invalid = value === "" || Number.isNaN(num) || num < 0 || num > qty + 1e-9;
  return (
    <Dialog open={!!lot} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="lot-adjust-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-base">{t("stock.adjustRemaining")} · {lot.lot_code}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{t("stock.adjustRemainingHint")}</p>
          <div className="grid grid-cols-2 gap-3 rounded-lg border border-border p-3 text-sm">
            <div><span className="text-muted-foreground">{td(lot.product_type)}</span></div>
            <div className="text-right font-mono">{t("stock.lotQty")}: {fmt(qty, 2)} {lot.unit}</div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="adjust-remaining">{t("common.remaining")} ({lot.unit})</Label>
            <Input id="adjust-remaining" type="number" min="0" max={qty} step="0.001" value={value}
              onChange={(e) => setValue(e.target.value)} data-testid="lot-adjust-input" />
            <p className="text-[11px] text-muted-foreground">{t("stock.adjustRange", { max: fmt(qty, 2), unit: lot.unit })}</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="adjust-note">{t("common.notes")}</Label>
            <Input id="adjust-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("stock.adjustNotePh")} data-testid="lot-adjust-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="lot-adjust-cancel">{t("common.cancel")}</Button>
          <Button onClick={() => onSave(lot, num, note)} disabled={invalid} data-testid="lot-adjust-save">{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LotDetailDialog({ detail, dslMap, freezerMap, onClose }) {
  const { t, td } = useT();
  if (!detail) return null;
  const l = detail;
  const rows = [
    [t("common.lot"), <span className="font-mono">{l.lot_code}</span>],
    [t("common.product"), td(l.product_type)],
    [t("common.unit"), l.unit],
    [t("common.quantity"), fmt(l.quantity, 2)],
    [t("common.remaining"), fmt(l.remaining, 2)],
    [t("stock.entryDate"), (l.created_at || "").slice(0, 10) || "-"],
    [t("stock.madeOn"), l.production_date || "-"],
    [t("common.expiry"), l.expiry_date ? formatDate(l.expiry_date) : "-"],
    ...((freezerMap && freezerMap[l.product_type]) ? [[t("stock.storageState"), <StorageBadge state={l.storage_state || "frozen"} />]] : []),
    [t("stock.fromProductionCol"), <span className="font-mono">{l.production_code || "-"}</span>],
  ];
  return (
    <Dialog open={!!detail} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="lot-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="font-mono text-sm">{l.lot_code}</span>
            <Badge variant="secondary">{td(l.product_type)}</Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="divide-y divide-border">
          {rows.map(([label, value], i) => (
            <div key={i} className="flex items-center justify-between gap-3 py-2 text-sm">
              <span className="text-muted-foreground">{label}</span>
              <span className="text-right font-medium">{value}</span>
            </div>
          ))}
          {l.notes ? (
            <div className="py-2 text-sm">
              <div className="text-muted-foreground">{t("common.notes")}</div>
              <div className="mt-1 whitespace-pre-wrap">{l.notes}</div>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Searchable source-lot picker for "Add stock lot → From existing lot".
// Search by lot code OR product name; each option shows "<units> x <package>" available.
function SourceLotCombobox({ sources, value, onSelect, td, t }) {
  const [open, setOpen] = useState(false);
  const selected = (sources || []).find((l) => l.id === value);
  const label = selected
    ? `${selected.lot_code} · ${td(selected.product_type)} · ${fmt(selected.remaining)} x ${selected.unit}`
    : t("stock.newStock");
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid="lot-source-select"
        >
          <span className="truncate">{label}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder={t("stock.searchLotOrProduct")} data-testid="lot-source-search" />
          <CommandList>
            <CommandEmpty>{t("stock.noLotMatch")}</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__new_stock__"
                onSelect={() => { onSelect("none"); setOpen(false); }}
                data-testid="lot-source-option-none"
              >
                <Check className={`mr-2 h-4 w-4 ${!value ? "opacity-100" : "opacity-0"}`} />
                {t("stock.newStock")}
              </CommandItem>
              {(sources || []).map((l) => (
                <CommandItem
                  key={l.id}
                  value={`${l.lot_code} ${td(l.product_type)} ${l.id}`}
                  onSelect={() => { onSelect(l.id); setOpen(false); }}
                  data-testid={`lot-source-option-${l.id}`}
                >
                  <Check className={`mr-2 h-4 w-4 shrink-0 ${value === l.id ? "opacity-100" : "opacity-0"}`} />
                  <span className="font-mono">{l.lot_code}</span>
                  <span className="mx-1.5 text-muted-foreground">·</span>
                  <span className="truncate">{td(l.product_type)}</span>
                  <span className="ml-auto whitespace-nowrap pl-3 font-mono text-xs text-muted-foreground">
                    {fmt(l.remaining)} x {l.unit}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}


function LotDialog({ dialog, productions, packagings, pkgMap, lots, dslMap, freezerMap, onClose, onSave }) {
  const { t, td } = useT();
  const { productTypes } = useProductTypes();
  const [f, setF] = useState(dialog);
  if (!f) return null;
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const rebatch = !!f.source_lot_id; // "from existing lot" mode
  const sourceLot = rebatch ? (lots || []).find((l) => l.id === f.source_lot_id) : null;
  const availableSources = (lots || []).filter((l) => Number(l.remaining) > 0);
  // Productions that already have a stock lot linked to them should not be offered again in the
  // "From production" picker (stock was already added from them).
  const usedProductionIds = new Set((lots || []).filter((l) => l.production_id).map((l) => l.production_id));
  const availableProductions = (productions || []).filter((p) => !usedProductionIds.has(p.id));
  const dsl = (dslMap || {})[f.product_type]; // delivery shelf life for this product (if configured)
  const isFreezer = !!(freezerMap || {})[f.product_type]; // product is kept in a freezer
  const showStorage = !rebatch && isFreezer; // storage state only applies to freezer products
  // Storage state is just a label on a new/edited lot — it does not change the expiry. The shorter
  // delivery shelf life is applied only when a lot is taken out of the freezer or repackaged.
  const setStorage = (v) => set("storage_state", v);
  // Restrict packaging to the product's fixed list (kg always allowed; keep current unit while editing).
  const opts = allowedPackagings(pkgMap, f.product_type, packagings);
  const setProduct = (v) => {
    const allowed = allowedPackagings(pkgMap, v, packagings).map((u) => u.label);
    setF((x) => ({ ...x, product_type: v, unit: allowed.includes(x.unit) ? x.unit : (allowed[0] || "kg") }));
  };
  const onSource = (sid) => {
    if (sid === "none") {
      setF((x) => ({ ...x, source_lot_id: null }));
      return;
    }
    const src = (lots || []).find((l) => l.id === sid);
    if (!src) return;
    const allowed = allowedPackagings(pkgMap, src.product_type, packagings).map((u) => u.label);
    // Repackaging into smaller portions gets a fresh expiry = today + delivery shelf life (if set).
    const dslSrc = (dslMap || {})[src.product_type];
    const today = new Date().toISOString().slice(0, 10);
    const parentExp = (src.expiry_date || "").slice(0, 10);
    const dslExp = dslSrc ? addDays(today, dslSrc) : "";
    // Repackaging never extends shelf life: the earlier of (today + delivery shelf life) and the parent's expiry.
    const capped = dslExp && parentExp ? (dslExp < parentExp ? dslExp : parentExp) : (dslExp || parentExp);
    setF((x) => ({
      ...x,
      source_lot_id: sid,
      production_id: "none",
      product_type: src.product_type,
      unit: (allowed.find((u) => u !== src.unit) || allowed[0] || src.unit),
      quantity: "",
      expiry_date: x.keep_source ? parentExp : capped,
      production_date: (src.production_date || "").slice(0, 10),
    }));
  };
  const parentExpiry = (sourceLot?.expiry_date || "").slice(0, 10);
  const setKeepSource = (v) => setF((x) => {
    const src = (lots || []).find((l) => l.id === x.source_lot_id);
    const pExp = (src?.expiry_date || "").slice(0, 10);
    if (v) return { ...x, keep_source: true, expiry_date: pExp || x.expiry_date };
    const dslSrc = src ? (dslMap || {})[src.product_type] : null;
    const dslExp = dslSrc ? addDays(new Date().toISOString().slice(0, 10), dslSrc) : "";
    const capped = dslExp && pExp ? (dslExp < pExp ? dslExp : pExp) : (dslExp || pExp);
    return { ...x, keep_source: false, expiry_date: capped || x.expiry_date };
  });
  const onProduction = (pid) => {
    if (pid === "none") { set("production_id", "none"); return; }
    const p = productions.find((x) => x.id === pid);
    setF((x) => ({
      ...x, production_id: pid,
      product_type: p?.product_type || x.product_type,
      expiry_date: (p?.expiration_date || x.expiry_date || "").slice(0, 10),
      production_date: (p?.date || x.production_date || "").slice(0, 10),
      entry_date: (p?.date || x.entry_date || "").slice(0, 10),
    }));
  };
  return (
    <Dialog open={!!dialog} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{f.id ? t("stock.editLot") : (rebatch ? t("stock.rebatchTitle") : t("stock.addLot"))}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!f.id && (
            <div className="space-y-1.5">
              <Label>{t("stock.fromExistingLot")}</Label>
              <SourceLotCombobox sources={availableSources} value={f.source_lot_id} onSelect={onSource} td={td} t={t} />
              {rebatch && <p className="text-[11px] text-muted-foreground">{t("stock.rebatchHint")}</p>}
            </div>
          )}
          {!f.id && !rebatch && (
            <div className="space-y-1.5">
              <Label>{t("stock.fromProduction")}</Label>
              <Select value={f.production_id} onValueChange={onProduction}>
                <SelectTrigger data-testid="lot-production-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("stock.manualEntry")}</SelectItem>
                  {availableProductions.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.production_code} · {td(p.product_type)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("common.product")}</Label>
              <Select value={f.product_type} onValueChange={setProduct} disabled={!!f.id || rebatch}>
                <SelectTrigger data-testid="lot-product-select"><SelectValue /></SelectTrigger>
                <SelectContent>{productTypes.map((p) => <SelectItem key={p} value={p}>{td(p)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{rebatch ? t("stock.newPackaging") : t("common.packaging")}</Label>
              <Select value={f.unit} onValueChange={(v) => set("unit", v)}>
                <SelectTrigger data-testid="lot-unit-select"><SelectValue /></SelectTrigger>
                <SelectContent>{opts.map((u) => <SelectItem key={u.label} value={u.label}>{u.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("common.quantity")}</Label>
              <Input type="number" className="text-right font-mono" value={f.quantity} onChange={(e) => set("quantity", e.target.value)} data-testid="lot-quantity-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("stock.expiryDate")}</Label>
              <Input type="date" value={f.expiry_date} max={rebatch && parentExpiry ? parentExpiry : undefined} disabled={rebatch && !!f.keep_source}
                onChange={(e) => set("expiry_date", e.target.value)} data-testid="lot-expiry-input" />
              {rebatch && !!f.keep_source && parentExpiry && (
                <p className="text-[11px] text-muted-foreground" data-testid="rebatch-expiry-inherited">{t("stock.rebatchExpiryInherited", { date: formatDate(parentExpiry) })}</p>
              )}
              {rebatch && !f.keep_source && parentExpiry && f.expiry_date && f.expiry_date > parentExpiry && (
                <p className="text-[11px] text-[hsl(var(--destructive))]" data-testid="rebatch-expiry-too-late">{t("stock.rebatchExpiryTooLate", { date: formatDate(parentExpiry) })}</p>
              )}
            </div>
          </div>
          {showStorage && (
            <div className="space-y-1.5">
              <Label>{t("stock.storageState")}</Label>
              <Select value={f.storage_state || "frozen"} onValueChange={setStorage}>
                <SelectTrigger data-testid="lot-storage-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="frozen">{t("stock.frozen")}</SelectItem>
                  <SelectItem value="refrigerated">{t("stock.refrigerated")}</SelectItem>
                </SelectContent>
              </Select>
              {(f.storage_state || "frozen") === "refrigerated" ? (
                <p className="text-[11px] text-muted-foreground" data-testid="lot-storage-hint">{t("stock.refrigeratedStateHint")}</p>
              ) : (
                <p className="text-[11px] text-muted-foreground">{t("stock.frozenHint")}</p>
              )}
            </div>
          )}
          {!rebatch && (
            <div className="space-y-1.5">
              <Label>{t("stock.entryDate")}</Label>
              <Input type="date" value={f.entry_date || ""} onChange={(e) => set("entry_date", e.target.value)} data-testid="lot-entry-date-input" />
              <p className="text-[11px] text-muted-foreground">{t("stock.entryDateHint")}</p>
            </div>
          )}
          {!rebatch && (
            <div className="space-y-1.5">
              <Label>{t("stock.madeOn")}</Label>
              <Input type="date" value={f.production_date || ""} onChange={(e) => set("production_date", e.target.value)} data-testid="lot-madeon-input" />
            </div>
          )}
          {rebatch && (
            <div className="flex items-start gap-2.5 rounded-lg border border-border bg-secondary/40 px-3 py-2.5" data-testid="rebatch-keep-source-row">
              <Checkbox
                id="keep-source"
                checked={!!f.keep_source}
                onCheckedChange={(v) => setKeepSource(v === true)}
                data-testid="rebatch-keep-source-checkbox"
                className="mt-0.5"
              />
              <div className="space-y-0.5">
                <Label htmlFor="keep-source" className="cursor-pointer text-sm font-medium">{t("stock.keepSourceLot")}</Label>
                <p className="text-[11px] text-muted-foreground">{t("stock.keepSourceLotHint")}</p>
              </div>
            </div>
          )}
          {rebatch && sourceLot && (() => {
            const net = Number((packagings.find((p) => p.label === f.unit) || {}).net_kg) || (f.unit === "kg" ? 1 : 0);
            const srcNet = Number((packagings.find((p) => p.label === sourceLot.unit) || {}).net_kg) || (sourceLot.unit === "kg" ? 1 : 0);
            if (net <= 0) {
              return <p className="text-[11px] text-[hsl(var(--warning))]" data-testid="rebatch-hint">{t("stock.kgHintNoNet", { unit: f.unit })}</p>;
            }
            const kg = (Number(f.quantity) || 0) * net;
            const availKg = Number(sourceLot.remaining) * (srcNet || 1);
            const over = kg > availKg + 1e-6;
            return (
              <div className="space-y-1">
                <p className={`text-[11px] ${over ? "text-[hsl(var(--destructive))]" : "text-muted-foreground"}`} data-testid="rebatch-hint">
                  {t("stock.rebatchUses", { kg: fmt(kg, 2), source: sourceLot.lot_code, avail: fmt(availKg, 2) })}
                </p>
                <p className="rounded-md bg-secondary/60 px-2.5 py-1.5 text-[11px]" data-testid="rebatch-result-preview">
                  {t("stock.rebatchResult", {
                    code: f.keep_source ? sourceLot.lot_code : t("stock.rebatchNewNumber"),
                    date: f.expiry_date ? formatDate(f.expiry_date) : "—",
                  })}
                </p>
              </div>
            );
          })()}
          {!f.id && !rebatch && f.unit !== "kg" && (() => {
            const net = Number((packagings.find((p) => p.label === f.unit) || {}).net_kg) || 0;
            if (net <= 0) {
              return <p className="text-[11px] text-[hsl(var(--warning))]" data-testid="lot-kg-hint">{t("stock.kgHintNoNet", { unit: f.unit })}</p>;
            }
            const kg = (Number(f.quantity) || 0) * net;
            return <p className="text-[11px] text-muted-foreground" data-testid="lot-kg-hint">{t("stock.kgHint", { kg: fmt(kg, 2), product: td(f.product_type) })}</p>;
          })()}
          <div className="space-y-1.5">
            <Label>{t("common.notes")}</Label>
            <Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="lot-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={() => onSave(f)} data-testid="lot-save-button">{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
